import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AddFilled, ArrowDownloadFilled } from '@fluentui/react-icons';
import { Box, Button, Card, useTheme } from '@mui/material';
import { useEffect, useState, useRef } from 'react';
import useManualEntriesStyles from './useManualEntriesStyles';
import { useTranslation } from 'react-i18next';
import { SDataGrid } from '@ui/components/common/grid/SDataGrid';
import { ConfirmDialog, ManualEntryDialog } from '@ui/components/Dialogs';
import { useDispatch, useSelector } from 'react-redux';
import { clearIncidentsByAssetId, fetchIncidentPdf, fetchIncidentsByAssetId, setPageNumber, setPageSize } from '@application/redux/asset-specific-incidents/asset-incidents-slice';
import { convertUtcToLocal } from '@application/utilities/date-time-formater';
import { toast } from 'react-toastify';
const dialogInitialStates = {
    show: false,
    variant: 'add',
    data: null
};
const ManualEntries = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { manualEntriesCardStyles } = useManualEntriesStyles();
    const dispatch = useDispatch();
    const { incidents, updateGrid, pageNumber, pageSize, loading } = useSelector((state) => state.IncidentByAssetId);
    const pathname = window.location.pathname;
    const segments = pathname.split('/');
    const id = segments[segments.length - 1];
    const isMountedRef = useRef(false);
    const prevValuesRef = useRef(null);
    const [systemDialog, setSystemDialog] = useState({
        show: false,
        variant: ''
    });
    useEffect(() => {
        if (!id)
            return;
        const currentValues = { id, pageNumber, pageSize, updateGrid };
        if (!isMountedRef.current) {
            isMountedRef.current = true;
            prevValuesRef.current = currentValues;
            dispatch(fetchIncidentsByAssetId({ assetId: id, isManuallyEntered: true, pageNumber, pageSize }));
            return;
        }
        if (prevValuesRef.current) {
            const prev = prevValuesRef.current;
            const shouldFetch = prev.id !== id ||
                prev.pageNumber !== pageNumber ||
                prev.pageSize !== pageSize ||
                prev.updateGrid !== updateGrid;
            if (shouldFetch) {
                prevValuesRef.current = currentValues;
                dispatch(fetchIncidentsByAssetId({ assetId: id, isManuallyEntered: true, pageNumber, pageSize }));
            }
        }
        return () => {
            dispatch(clearIncidentsByAssetId());
            isMountedRef.current = false;
            prevValuesRef.current = null;
        };
    }, [dispatch, id, updateGrid, pageNumber, pageSize]);
    const [manualEntryDialog, setManualEntryDialog] = useState(dialogInitialStates);
    const [isDeleting, setIsDeleting] = useState(false);
    const rows = incidents?.data?.data?.map((r) => ({
        id: r.id || '',
        systemName: r.systemName || '',
        licensePlate: r.licensePlate || '',
        incidentDate: r.incidentDate || '',
        score: r.score ?? '',
        location: r.location || ''
    })) || [];
    const columns = [
        {
            field: 'licensePlate',
            headerName: t('License#'),
            minWidth: 154,
            flex: 1,
            editable: false,
            type: 'string'
        },
        {
            field: 'incidentDate',
            headerName: t('DateTime'),
            minWidth: 154,
            flex: 1,
            editable: false,
            type: 'string',
            renderCell: (params) => {
                return convertUtcToLocal(params.value);
            }
        },
        {
            field: 'score',
            headerName: t('SeverityScore'),
            minWidth: 154,
            flex: 1,
            editable: false,
            type: 'number',
            align: 'left',
            headerAlign: 'left'
        },
        {
            field: 'location',
            headerName: t('Location'),
            minWidth: 198,
            flex: 1,
            editable: false,
            type: 'string'
        }
    ];
    async function handlePDFReport() {
        try {
            dispatch(fetchIncidentPdf(id))
                .unwrap()
                .then(() => {
                toast.success('PDF downloaded successfully');
            })
                .catch((err) => {
                toast.error('No data found for todays report');
            });
        }
        catch (error) {
            toast.error(`Failed to download PDF report: ${error}`);
        }
    }
    return (_jsxs(Card, { className: 'assets-manual-entries-grid-card', sx: manualEntriesCardStyles, children: [_jsxs(Box, { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, children: [_jsx(Button, { variant: 'contained', startIcon: _jsx(ArrowDownloadFilled, { fontSize: 20 }), onClick: handlePDFReport, loading: loading, disabled: !incidents?.data?.totalRecords, sx: {
                            backgroundColor: '#f9f9f9',
                            color: 'black',
                            border: '1px solid black',
                            '&:hover': {
                                backgroundColor: '#f0f0f0'
                            }
                        }, children: t('ExportManualEntriesToPdf') }), _jsx(Button, { color: 'inherit', variant: 'contained', startIcon: _jsx(AddFilled, { fontSize: 20 }), onClick: () => {
                            setManualEntryDialog((prev) => ({
                                ...prev,
                                show: true,
                                variant: 'add'
                            }));
                        }, children: t('CreateEntry') })] }), _jsx(Box, { className: 'table-container', children: _jsx(SDataGrid, { rows: rows, columns: columns, serverSidePagination: true, totalRows: incidents?.data?.totalRecords, pageSizeOptions: [10, 20, 50, 100], loading: loading, initialState: {
                        pagination: {
                            paginationModel: {
                                pageSize: incidents?.data.pageSize || 10,
                                page: incidents?.data.pageNumber || 0
                            }
                        }
                    }, onPaginationModelChange: (model) => {
                        dispatch(setPageNumber(model.page + 1));
                        dispatch(setPageSize(model.pageSize));
                    }, filters: {}, setFilters: () => { } }) }), manualEntryDialog.show && ['add', 'edit'].includes(manualEntryDialog.variant) && (_jsx(ManualEntryDialog, { isOpen: manualEntryDialog.show, onClose: () => setManualEntryDialog(dialogInitialStates), variant: manualEntryDialog.variant })), manualEntryDialog.show && manualEntryDialog.variant === 'delete' && (_jsx(ConfirmDialog, { isOpen: manualEntryDialog.show, onClose: () => setManualEntryDialog(dialogInitialStates), onConfirm: () => { }, loading: isDeleting, actionText: t('DeleteManualEntryText'), screenName: manualEntryDialog?.data?.licensePlate ? manualEntryDialog.data.licensePlate : '' }))] }));
};
export default ManualEntries;
